
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {RefreshTokenResponse} from './RefreshTokenResponse';
import {LoginInfo} from './LoginInfo';
import {FieldModelLoginInfo} from './LoginInfo';

/**
 * RefreshTokenRequest struct
 */
class RefreshTokenRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!number=} protocolVersionMajor
   * @param {!number=} protocolVersionMinor
   * @param {!LoginInfo=} agentInfo
   * @param {!number=} login
   * @param {!string=} refreshToken
   * @constructor
   */
  constructor (parent = new Request(), protocolVersionMajor = 0, protocolVersionMinor = 0, agentInfo = new LoginInfo(), login = new UInt64(0, 0), refreshToken = '') {
    super()
    super.copy(parent)
    this.ProtocolVersionMajor = protocolVersionMajor
    this.ProtocolVersionMinor = protocolVersionMinor
    this.AgentInfo = agentInfo
    this.Login = login
    this.RefreshToken = refreshToken
  }

  /**
   * Copy struct (shallow copy)
   * @this {!RefreshTokenRequest}
   * @param {!RefreshTokenRequest} other Other struct
   * @returns {!RefreshTokenRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.ProtocolVersionMajor != null) {
      this.ProtocolVersionMajor = other.ProtocolVersionMajor
    } else {
      this.ProtocolVersionMajor = null
    }
    if (other.ProtocolVersionMinor != null) {
      this.ProtocolVersionMinor = other.ProtocolVersionMinor
    } else {
      this.ProtocolVersionMinor = null
    }
    if (other.AgentInfo != null) {
      this.AgentInfo = LoginInfo.fromObject(other.AgentInfo)
    } else {
      this.AgentInfo = null
    }
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.RefreshToken != null) {
      this.RefreshToken = other.RefreshToken
    } else {
      this.RefreshToken = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!RefreshTokenRequest}
   * @returns {!RefreshTokenRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new RefreshTokenRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new RefreshTokenRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!RefreshTokenRequest}
   * @param {!RefreshTokenRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof RefreshTokenRequest)) {
      throw new TypeError('Instance of RefreshTokenRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!RefreshTokenRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      ProtocolVersionMajor: ((this.ProtocolVersionMajor != null) ? this.ProtocolVersionMajor : null),
      ProtocolVersionMinor: ((this.ProtocolVersionMinor != null) ? this.ProtocolVersionMinor : null),
      AgentInfo: ((this.AgentInfo != null) ? this.AgentInfo : null),
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      RefreshToken: ((this.RefreshToken != null) ? this.RefreshToken : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return RefreshTokenRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!RefreshTokenRequest} other Object value
   * @returns {!RefreshTokenRequest} Created struct
   */
  static fromObject (other) {
    return new RefreshTokenRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!RefreshTokenRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return RefreshTokenRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!RefreshTokenRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5534
  }
}

export { RefreshTokenRequest };

/**
 * Fast Binary Encoding RefreshTokenRequest field model
 */
class FieldModelRefreshTokenRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.ProtocolVersionMajor = new fbe.FieldModelUInt16(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.ProtocolVersionMinor = new fbe.FieldModelUInt16(buffer, this.ProtocolVersionMajor.fbeOffset + this.ProtocolVersionMajor.fbeSize)
    this.AgentInfo = new FieldModelLoginInfo(buffer, this.ProtocolVersionMinor.fbeOffset + this.ProtocolVersionMinor.fbeSize)
    this.Login = new fbe.FieldModelUInt64(buffer, this.AgentInfo.fbeOffset + this.AgentInfo.fbeSize)
    this.RefreshToken = new fbe.FieldModelString(buffer, this.Login.fbeOffset + this.Login.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelRefreshTokenRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelRefreshTokenRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelRefreshTokenRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.ProtocolVersionMajor.fbeSize + this.ProtocolVersionMinor.fbeSize + this.AgentInfo.fbeSize + this.Login.fbeSize + this.RefreshToken.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelRefreshTokenRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.ProtocolVersionMajor.fbeExtra + this.ProtocolVersionMinor.fbeExtra + this.AgentInfo.fbeExtra + this.Login.fbeExtra + this.RefreshToken.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelRefreshTokenRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelRefreshTokenRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelRefreshTokenRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5534
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelRefreshTokenRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelRefreshTokenRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.ProtocolVersionMajor.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ProtocolVersionMajor.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ProtocolVersionMajor.fbeSize

    if ((fbeCurrentSize + this.ProtocolVersionMinor.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ProtocolVersionMinor.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ProtocolVersionMinor.fbeSize

    if ((fbeCurrentSize + this.AgentInfo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AgentInfo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AgentInfo.fbeSize

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.RefreshToken.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RefreshToken.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RefreshToken.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelRefreshTokenRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelRefreshTokenRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelRefreshTokenRequest}
   * @param {!RefreshTokenRequest} fbeValue Default value, defaults is new RefreshTokenRequest()
   * @returns {!RefreshTokenRequest} RefreshTokenRequest value
   */
  get (fbeValue = new RefreshTokenRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelRefreshTokenRequest}
   * @param {!RefreshTokenRequest} fbeValue RefreshTokenRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.ProtocolVersionMajor.fbeSize) <= fbeStructSize) {
      fbeValue.ProtocolVersionMajor = this.ProtocolVersionMajor.get()
    } else {
      fbeValue.ProtocolVersionMajor = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ProtocolVersionMajor.fbeSize

    if ((fbeCurrentSize + this.ProtocolVersionMinor.fbeSize) <= fbeStructSize) {
      fbeValue.ProtocolVersionMinor = this.ProtocolVersionMinor.get()
    } else {
      fbeValue.ProtocolVersionMinor = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ProtocolVersionMinor.fbeSize

    if ((fbeCurrentSize + this.AgentInfo.fbeSize) <= fbeStructSize) {
      fbeValue.AgentInfo = this.AgentInfo.get()
    } else {
      fbeValue.AgentInfo = new LoginInfo()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AgentInfo.fbeSize

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.RefreshToken.fbeSize) <= fbeStructSize) {
      fbeValue.RefreshToken = this.RefreshToken.get()
    } else {
      fbeValue.RefreshToken = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RefreshToken.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelRefreshTokenRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelRefreshTokenRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelRefreshTokenRequest}
   * @param {!RefreshTokenRequest} fbeValue RefreshTokenRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelRefreshTokenRequest}
   * @param {RefreshTokenRequest} fbeValue RefreshTokenRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.ProtocolVersionMajor.set(fbeValue.ProtocolVersionMajor)
    this.ProtocolVersionMinor.set(fbeValue.ProtocolVersionMinor)
    this.AgentInfo.set(fbeValue.AgentInfo)
    this.Login.set(fbeValue.Login)
    this.RefreshToken.set(fbeValue.RefreshToken)
  }
}

export { FieldModelRefreshTokenRequest };

/**
 * Fast Binary Encoding RefreshTokenRequest model
 */
class RefreshTokenRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelRefreshTokenRequest(this.buffer, 4)
  }

  /**
   * Get the RefreshTokenRequest model
   * @this {!RefreshTokenRequestModel}
   * @returns {!FieldModelRefreshTokenRequest} model RefreshTokenRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!RefreshTokenRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!RefreshTokenRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return RefreshTokenRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!RefreshTokenRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelRefreshTokenRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!RefreshTokenRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!RefreshTokenRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!RefreshTokenRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!RefreshTokenRequestModel}
   * @param {!RefreshTokenRequest} value RefreshTokenRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!RefreshTokenRequestModel}
   * @param {!RefreshTokenRequest} value RefreshTokenRequest value, defaults is new RefreshTokenRequest()
   * @return {!object} Deserialized RefreshTokenRequest value and its size
   */
  deserialize (value = new RefreshTokenRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new RefreshTokenRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new RefreshTokenRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!RefreshTokenRequestModel}
   * @param {!number} prev Previous RefreshTokenRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { RefreshTokenRequestModel };
RefreshTokenRequest.__has_response = true;
RefreshTokenRequest.__response_class = RefreshTokenResponse;
